import { combineRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import { useText } from '../../../utils/hooks/use_text';
import { InputPropsHelper } from '../form/form.helpers';
import { useFormContext } from '../index';
import { CellulaInputTextRoot, InputTextRootProps } from './_input_text_root.component';

interface InputTextProps extends InputTextRootProps {
	options?: RegisterOptions;
}

function CellulaInputText(props: InputTextProps, ref: ForwardedRef<HTMLInputElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (formContext == null || props.name == null) {
		return <CellulaInputTextRoot {...props} ref={ref} />;
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				...props.options,
				onChange: props.onChange,
				onBlur: props.onBlur,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				return (
					<CellulaInputTextRoot
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						readOnly={inputPropsHelper.isReadOnly()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						value={field.value ?? ''}
						defaultValue={undefined}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to provide a complete set of elements to let the user input
 * content. Including label, input field, error handling, helper text, etc.
 */
const InputText = forwardRef(CellulaInputText);

export type { InputTextProps };
export { InputText };
